import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { api, track } from "@/lib/api";
import ScratchCard from "@/components/ScratchCard";
import Particles from "@/components/Particles";
import Countdown from "@/components/Countdown";
import Timeline from "@/components/Timeline";
import LaunchOverlay from "@/components/LaunchOverlay";
import { toast } from "sonner";
import {
  Sparkles,
  Eye,
  Volume2,
  VolumeX,
  Gift,
  ExternalLink,
} from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" } }),
};

export default function Landing() {
  const [data, setData] = useState(null);
  const [revealSignal, setRevealSignal] = useState(0);
  const [showLaunch, setShowLaunch] = useState(false);
  const [returningMsg, setReturningMsg] = useState(null);
  const [soundOn, setSoundOn] = useState(false);
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    const sim = new URLSearchParams(window.location.search).get("sim");
    const url = sim ? `/config/public?simulateDay=${sim}` : "/config/public";
    const { data } = await api.get(url);
    setData(data);
    return data;
  }, []);

  useEffect(() => {
    track("page_view");
    load().then((d) => {
      // returning visitor logic
      const last = localStorage.getItem("gaelon_lastVisitDay");
      const today = d.state.currentDay;
      if (last !== null) {
        const lastDay = parseInt(last, 10);
        const newParts = today - lastDay;
        if (newParts > 0) {
          track("returning_visitor", { newParts });
          setReturningMsg(
            newParts === 1
              ? "Uma nova parte do Repositor Online foi liberada desde sua última visita."
              : `${newParts} novas partes foram reveladas desde sua última visita.`
          );
        }
      }
      localStorage.setItem("gaelon_lastVisitDay", String(today));

      if (d.state.launched) {
        const seen = localStorage.getItem("gaelon_launchAnimationSeen");
        if (!seen) {
          setShowLaunch(true);
          localStorage.setItem("gaelon_launchAnimationSeen", "true");
          track("project_completed");
        }
      }
    });
  }, [load]);

  const scrollToCard = () => {
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleReveal = () => {
    setRevealSignal((s) => s + 1);
    track("daily_fragment_revealed", { method: "button" });
  };

  const handleFragmentComplete = () => {
    track("scratch_completed");
    toast.success("Fragmento revelado!", { description: randomEgg(data) });
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  const { config, state } = data;

  return (
    <div className="noise-overlay relative min-h-screen overflow-x-hidden bg-background text-foreground">
      <Particles />

      {showLaunch && (
        <LaunchOverlay
          logoUrl={config.projectLogoUrl}
          finalUrl={config.finalUrl}
          onClose={() => setShowLaunch(false)}
        />
      )}

      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-40 glass" data-testid="site-header">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            {config.gaelonLogoUrl ? (
              <img
                src={config.gaelonLogoUrl}
                alt="GAELON"
                className="h-7 w-auto object-contain"
                data-testid="header-gaelon-logo"
              />
            ) : (
              <span className="font-display text-lg font-semibold tracking-[0.2em] metallic-sheen animate-shimmer">
                GAELON
              </span>
            )}
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-white/35">
              Tecnologia
            </span>
          </div>
          <div
            className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1"
            data-testid="dev-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/70">Em desenvolvimento</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-5 pt-32 pb-24">
        {/* returning banner */}
        {returningMsg && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto mb-10 flex max-w-2xl items-center justify-between gap-4 rounded-xl glass px-5 py-3"
            data-testid="returning-banner"
          >
            <div className="flex items-center gap-3">
              <Gift className="h-4 w-4 text-white/70" />
              <div>
                <p className="text-sm text-white">Bem-vindo de volta.</p>
                <p className="text-xs text-white/50">{returningMsg}</p>
              </div>
            </div>
            <button
              onClick={() => {
                handleReveal();
                scrollToCard();
              }}
              data-testid="see-changes-btn"
              className="whitespace-nowrap rounded-full bg-white/10 px-4 py-1.5 text-xs text-white transition-colors hover:bg-white/20"
            >
              Ver o que mudou
            </button>
          </motion.div>
        )}

        {/* HERO */}
        <div className="text-center">
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0}
            className="text-[11px] uppercase tracking-[0.35em] text-white/40"
          >
            Uma solução desenvolvida pela Gaelon
          </motion.p>
          <motion.h1
            variants={fade}
            initial="hidden"
            animate="show"
            custom={1}
            className="mx-auto mt-6 max-w-3xl font-display text-4xl font-light leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl metallic-text"
          >
            {config.heroTitle}
          </motion.h1>
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={2}
            className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/55"
          >
            {config.heroSubtitle}
          </motion.p>
          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-4 inline-flex items-center gap-2 text-xs text-white/40"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {config.heroTagline}
          </motion.div>
        </div>

        {/* SCRATCH CARD */}
        <motion.div
          ref={cardRef}
          variants={fade}
          initial="hidden"
          animate="show"
          custom={4}
          className="mx-auto mt-16 max-w-4xl"
        >
          <div className="relative rounded-2xl glass scratch-glow p-2 sm:p-3">
            <ScratchCard
              logoUrl={config.projectLogoUrl}
              fragmentCount={config.fragmentCount}
              totalDays={config.totalDays}
              currentDay={state.currentDay}
              revealSequence={config.revealSequence}
              launched={state.launched}
              revealSignal={revealSignal}
              onFragmentComplete={handleFragmentComplete}
              hiddenWords={config.hiddenWords}
              soundOn={soundOn}
            />
          </div>

          {/* fragment of the day + controls */}
          <div className="mt-5 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-white/60">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/40" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white/80" />
              </span>
              Seu novo fragmento já está disponível.
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => {
                  handleReveal();
                  track("scratch_started", { method: "auto" });
                }}
                data-testid="reveal-available-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/80 transition-colors hover:border-white/30 hover:text-white"
              >
                <Eye className="h-3.5 w-3.5" /> Revelar área disponível
              </button>
              <button
                onClick={() => setSoundOn((s) => !s)}
                data-testid="sound-toggle-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs text-white/50 transition-colors hover:text-white/80"
              >
                {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                {soundOn ? "Som ativado" : "Ativar som"}
              </button>
            </div>
          </div>
        </motion.div>

        {/* PROGRESS */}
        <div className="mx-auto mt-20 max-w-3xl text-center" data-testid="progress-section">
          <div className="flex items-baseline justify-center gap-3">
            <span className="font-mono text-sm tracking-widest text-white/50">
              DIA {String(state.currentDay).padStart(2, "0")} DE {config.totalDays}
            </span>
            <span className="text-white/20">·</span>
            <span className="font-mono text-sm tracking-widest metallic-text">
              {state.progressPercentage}% REVELADO
            </span>
          </div>
          <div className="mx-auto mt-5 h-px w-full max-w-xl bg-white/10">
            <motion.div
              className="h-px bg-gradient-to-r from-white/40 via-white to-white/40"
              initial={{ width: 0 }}
              animate={{ width: `${state.progressPercentage}%` }}
              transition={{ duration: 1.4, ease: "easeOut" }}
            />
          </div>
          <p className="mt-6 font-display text-lg font-light text-white/80">{state.phaseMessage}</p>
          <div className="mt-4 flex items-center justify-center gap-6 text-xs text-white/40">
            <span>{state.completedDays} etapas concluídas</span>
            <span>·</span>
            <span>{state.remainingDays} etapas restantes</span>
          </div>
        </div>

        {/* COUNTDOWN */}
        {!state.launched && (
          <div className="mt-24 text-center" data-testid="countdown-section">
            <p className="mb-8 text-[11px] uppercase tracking-[0.35em] text-white/40">Revelação completa em</p>
            <Countdown
              targetIso={state.launchTargetIso}
              serverIso={state.serverTime}
              onComplete={() => load()}
            />
          </div>
        )}

        {/* IMPACT + STATUS + TIMELINE */}
        <div className="mt-28 grid gap-12 md:grid-cols-2">
          <div>
            <p className="font-display text-2xl font-light leading-snug text-white/85">
              Não estamos apenas contando os dias.
              <br />
              <span className="metallic-text">Estamos revelando a construção.</span>
            </p>
            <div className="mt-8 flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-[0.25em] text-white/40">Status atual</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                {config.status}
              </span>
            </div>
            <p className="mt-6 text-xs text-white/35">Última evolução — {config.lastUpdateText}</p>
          </div>
          <div className="rounded-2xl glass p-6">
            <p className="mb-5 text-[11px] uppercase tracking-[0.25em] text-white/40">Timeline do desenvolvimento</p>
            <Timeline steps={config.timeline || []} />
          </div>
        </div>

        {state.launched && (
          <div className="mt-16 text-center">
            <a
              href={config.finalUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="enter-platform-btn"
              onClick={() => track("launch_button_clicked")}
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 text-sm font-medium text-black transition-transform hover:scale-[1.03]"
            >
              Entrar na plataforma <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}
      </main>

      {/* FOOTER */}
      <footer className="relative z-10 border-t border-white/10 py-10 text-center" data-testid="footer">
        <p className="font-display text-sm tracking-wide text-white/70">{config.projectName}</p>
        <p className="mt-1 text-xs text-white/40">
          Uma solução desenvolvida pela Gaelon Tecnologia e Negócios Digitais.
        </p>
        <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-white/25">{config.projectCode}</p>
        <p className="mt-4 text-[11px] text-white/30">
          © 2026 Gaelon Tecnologia e Negócios Digitais LTDA. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
}

function randomEgg(data) {
  const eggs = data?.config?.easterEggs || [];
  if (!eggs.length) return "Cada dia revela uma nova peça.";
  return eggs[Math.floor(Math.random() * eggs.length)];
}
